import { computed, reactive, ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { OrderItem, Variant, PowerQuantity } from '@/models/webshop.model';
import { Patient } from '@/models/patient.model';
import { IOption, ISuggestion } from '@/lib';
import { WEBSHOP_MIN_PRODUCT_QUANTITY } from '@/constants';
import { useWebshopCartStore } from '@/stores/webshopCart.store';
import { useNotificationStore } from '@/stores/notification.store';
import { useSessionStore } from '@/stores/session.store';
import { debounce } from 'lodash-es';
import { formatPower } from '@/lib/helpers/format-power';
import { capitalizeBoolean, translateBoolean } from '@/lib/helpers/string.helper';
import {
  getCategoryFallbackImageText
} from '@/constants/webshop';
import { analyzeMergeOperation } from '@/lib/helpers/order-item-merge.helper';
import { formatVariantLabel, sortVariantsForPackOptions } from '@/lib/helpers/variant-label.helper';
import { createOrganizationAwarePatientService } from '@/services/organization-aware-patient.service';

export interface OrderCardEmits {
  (e: 'remove-item', item: OrderItem): void;
  (e: 'update-item', item: OrderItem): void;
  (e: 'select-item', data: { item: OrderItem; selected: boolean }): void;
}

export function useOrderCard(props: {
  item: OrderItem;
  isLoading: boolean;
  category: string;
  editable: boolean;
  isSelected?: boolean;
}, emit: OrderCardEmits, options?: {
  useCartStore?: boolean;
  onPatientSettingChange?: (val: boolean) => void;
}) {
  const { t } = useI18n();
  const webshopCartStore = options?.useCartStore ? useWebshopCartStore() : null;
  const notificationStore = options?.useCartStore ? useNotificationStore() : null;

  const isLoading = ref(false);
  const isSelected = ref(props.isSelected || false);

  watch(() => props.isSelected, (newVal) => {
    isSelected.value = Boolean(newVal);
  }, { immediate: true });

  const isUserEditingQuantity = ref(false);
  const isUserEditingReference = ref(false);
  const isPatientSetting = ref(false);

  watch(() => props.isLoading, (newVal) => {
    if (newVal === false && isPatientSetting.value) {
      isPatientSetting.value = false;
    }
  });

  // Notify parent component if callback provided
  if (options?.onPatientSettingChange) {
    watch(isPatientSetting, (val) => {
      options.onPatientSettingChange?.(val);
    }, { immediate: true });
  }

  const sessionStore = useSessionStore();
  const patientService = createOrganizationAwarePatientService(
    computed(() => sessionStore.currentOrganisationId || '')
  );

  const { patient, patientSearchText, patientSuggestions, isLoading: isPatientSearchLoading } = patientService.state;

  onUnmounted(() => {
    patientService.cleanup();
  });

  const editableFields = reactive({
    quantity: Number(props.item.quantity),
    reference: props.item.reference || '',
    packValue: String(props.item.variantOptionValue),
    patientId: props.item.patientId || null
  });

  const safeQuantity = computed({
    get: () => editableFields.quantity,
    set: (val: number | string) => {
      const parsed = Number(val);
      editableFields.quantity = !val || isNaN(parsed) || parsed < WEBSHOP_MIN_PRODUCT_QUANTITY
        ? WEBSHOP_MIN_PRODUCT_QUANTITY
        : parsed;
    }
  });

  const displayOptions = computed<Array<{key: string; label: string; value: string}>>(() => {
    try {
      const options = JSON.parse(props.item.options || '{}') as Record<string, {label?: string, value?: string} | string | null>;

      const entries = Object.entries(options)
        .filter(([key, value]) => {
          if (key === 'Pack') {
            return false;
          }

          if (key === 'Power' && props.item.isStock) {
            return false;
          }

          return value !== null && typeof value !== 'undefined';
        })
        .map(([key, value]) => {
          let displayValue: string;
          if (key.toLowerCase() === 'power' && value && typeof value === 'object' && 'value' in value) {
            const num = parseFloat(String(value.value));
            displayValue = isFinite(num) ? (num > 0 ? `+${num.toFixed(2)}` : num.toFixed(2)) : String(value.value);
          } else if (value && typeof value === 'object' && 'label' in value) {
            displayValue = key === 'TrialLens'
              ? translateBoolean(value.label || '', t)
              : capitalizeBoolean(value.label || '');
          } else {
            displayValue = key === 'TrialLens'
              ? translateBoolean(String(value), t)
              : capitalizeBoolean(String(value));
          }

          return {
            key,
            label: t(`platform.webshop.${key.toLowerCase()}`, key),
            value: displayValue
          };
        });

      // Sort by lensProperties order from MLS API if available
      const lensProperties = props.item.productOptions?.lensProperties;

      if (lensProperties && lensProperties.length > 0) {
        const propertyOrder = lensProperties.map((p) => p.name);

        return entries.sort((a, b) => {
          const aIndex = propertyOrder.indexOf(a.key);
          const bIndex = propertyOrder.indexOf(b.key);

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) {
            return -1;
          }
          if (bIndex !== -1) {
            return 1;
          }
          return 0;
        });
      }

      // No lensProperties available - preserve original order from options JSON
      return entries;
    } catch (error) {
      logError('Error parsing item options');
      return [];
    }
  });

  const packOptions = computed((): IOption[] => {
    const variants = props.item.productOptions?.variants || [];
    const sorted = sortVariantsForPackOptions(variants);
    return sorted.map((variant) => ({
      label: formatVariantLabel(variant),
      // Use code as primary identifier, fallback to publicIdentifier
      value: String(variant.code ?? variant.publicIdentifier)
    }));
  });

  const parsePowerQuantities = (powerQuantitiesData: string | PowerQuantity[] | undefined): PowerQuantity[] => {
    if (!powerQuantitiesData) {
      return [];
    }

    if (Array.isArray(powerQuantitiesData)) {
      return powerQuantitiesData.map((item) => ({
        ...item,
        power: formatPower(item.power?.trim() || '')
      }));
    }

    try {
      const parsed = JSON.parse(powerQuantitiesData);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(
            (item) =>
              item &&
              typeof item === 'object' &&
              'power' in item &&
              'quantity' in item &&
              typeof item.power === 'string' &&
              typeof item.quantity === 'number'
          )
          .map((item) => ({
            ...item,
            power: formatPower(item.power?.trim() || '')
          }));
      }
      return [];
    } catch (e) {
      logError('Error parsing power quantities');
      return [];
    }
  };

  const parsedPowerQuantities = computed<PowerQuantity[]>(() => parsePowerQuantities(props.item.powerQuantities));

  const calculateTotalQuantity = (powerQuantities: PowerQuantity[]) =>
    powerQuantities.reduce((sum, pq) => sum + (pq.quantity || 0), 0);

  const logError = (msg: string) => {
    if (notificationStore) {
      notificationStore.addErrorNotification({
        title: t('platform.common.failure'),
        label: msg
      });
    }
  };

  const showNotification = (success: boolean) => {
    if (!notificationStore) {
      return;
    }

    if (success) {
      notificationStore.addSuccessNotification({
        title: t('platform.common.success'),
        label: t('platform.webshop.notifications.item-updated'),
        autoDismiss: true,
        dismissAfter: 3000
      });
    } else {
      notificationStore.addErrorNotification({
        title: t('platform.common.failure'),
        label: t('platform.webshop.notifications.item-updated-failed')
      });
    }
  };


  const validateReference = (event: Event) => {
    let input = (event.target as HTMLInputElement).value;
    input = input.replace(/[\r\n]+/g, '');
    input = input.replace(/[<>'"&]/g, '');
    editableFields.reference = input.slice(0, 30);
  };

  const searchPatients = async () => {
    await patientService.searchPatients(patientSearchText.value);
  };

  const debouncedPatientSearch = debounce(searchPatients, 500);

  const handlePatientChange = async (suggestion: ISuggestion | string) => {
    const isSelectingPatient = typeof suggestion === 'object' && suggestion !== null && 'id' in suggestion;

    if (isSelectingPatient && props.editable) {
      isPatientSetting.value = true;
      try {
        await patientService.selectPatient(suggestion);
        editableFields.patientId = patient.value?.id || null;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        logError(`Failed to fetch patient details: ${message}`);
        editableFields.patientId = null;
      } finally {
        isPatientSetting.value = false;
      }
      patientSuggestions.value = [];
    } else if (typeof suggestion === 'string' && props.editable) {
      patientSearchText.value = suggestion;
      if (suggestion === '') {
        clearPatientData();
        patientSuggestions.value = [];
      } else {
        debouncedPatientSearch();
      }
    }
  };

  const getVariantByValue = (value: string): Variant | undefined =>
    props.item.productOptions?.variants?.find((p) =>
      String(p.code) === value || String(p.publicIdentifier) === value
    );

  const getCurrentItemState = (): OrderItem => {
    const selectedVariant = getVariantByValue(editableFields.packValue);
    const productOptions = {
      category: props.item.productOptions?.category,
      variants: props.item.productOptions?.variants || [],
      powers: props.item.productOptions?.powers || null,
      image: props.item.product?.image || props.item.productOptions?.image || null
    };

    return {
      ...props.item,
      quantity: Number(editableFields.quantity),
      reference: editableFields.reference || null,
      patientId: patient.value?.id || null,
      variantOptionValue: selectedVariant
        ? (selectedVariant.code ?? selectedVariant.publicIdentifier)
        : editableFields.packValue,
      variantOptionLabel: selectedVariant
        ? formatVariantLabel(selectedVariant)
        : (packOptions.value.find((p) => p.value === editableFields.packValue)?.label || ''),
      productOptions
    };
  };

  const handleSelectionChange = (value: boolean) => {
    isSelected.value = value;
    emit('select-item', { item: props.item, selected: value });
  };

  const triggerUpdate = () => {
    const currentItemState = getCurrentItemState();

    if (
      currentItemState.quantity < WEBSHOP_MIN_PRODUCT_QUANTITY ||
      editableFields.quantity < WEBSHOP_MIN_PRODUCT_QUANTITY
    ) {
      editableFields.quantity = WEBSHOP_MIN_PRODUCT_QUANTITY;
      return;
    }

    if (currentItemState.reference !== editableFields.reference) {
      editableFields.reference = currentItemState.reference || '';
    }

    emit('update-item', currentItemState);
  };

  const handleRemoveItem = async () => {
    const currentItemState = getCurrentItemState();

    if (options?.useCartStore && webshopCartStore) {
      if (isLoading.value) {
        return;
      }
      try {
        isLoading.value = true;
        await webshopCartStore.removeItem(currentItemState.id);
      } catch (error) {
        if (notificationStore) {
          notificationStore.addErrorNotification({
            title: t('platform.common.failure'),
            label: t('platform.webshop.notifications.item-removed-failed')
          });
        }
      } finally {
        isLoading.value = false;
      }
    } else {
      emit('remove-item', currentItemState);
    }
  };

  const handlePowerQuantitiesUpdate = (updatedPowerQuantities: { power: string; quantity: number }[]) => {
    const totalQuantity = updatedPowerQuantities.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQuantity === 0) {
      handleRemoveItem();
    } else {
      const updatedItem = { ...props.item };
      updatedItem.powerQuantities = JSON.stringify(updatedPowerQuantities);

      if (options?.useCartStore) {
        handleUpdateItem(updatedItem);
      } else {
        emit('update-item', updatedItem);
      }
    }
  };

  const getFallbackImage = (category: string): string => {
    const text = getCategoryFallbackImageText(category);
    return `https://placehold.co/350x180/EEEEEE/AAAAAA?text=${text}`;
  };

  watch(() => props.item.quantity, (newVal) => {
    if (isUserEditingQuantity.value) {
      return;
    }

    const parsed = Number(newVal);
    const safeQty = parsed >= WEBSHOP_MIN_PRODUCT_QUANTITY ? parsed : WEBSHOP_MIN_PRODUCT_QUANTITY;

    if (editableFields.quantity !== safeQty) {
      editableFields.quantity = safeQty;
    }
  });

  watch(() => props.item.reference, (newVal) => {
    if (isUserEditingReference.value) {
      return;
    }

    const currentRef = newVal || '';
    if (currentRef !== editableFields.reference) {
      editableFields.reference = currentRef;
    }
  });

  watch(() => props.item.variantOptionValue, (newVal) => {
    const pack = String(newVal);
    if (pack !== editableFields.packValue) {
      editableFields.packValue = pack;
    }
  });

  const formatPatientDisplay = (patientData: Patient, isDeleted: boolean): string => {
    const base = `${patientData.first_name} ${patientData.last_name} ${patientData.mrn ? `- ID: ${patientData.mrn}` : ''}`;
    return isDeleted ? `${base} (${t('platform.webshop.deleted')})` : base;
  };

  const handleDeletedPatient = (patientData: Patient) => {
    patient.value = patientData;
    if (props.editable) {
      patientSearchText.value = '';
      editableFields.patientId = null;
    } else {
      patientSearchText.value = formatPatientDisplay(patientData, true);
    }
  };

  const clearPatientData = () => {
    patient.value = null;
    patientSearchText.value = '';
    editableFields.patientId = null;
  };

  const shouldMakeApiCall = (): boolean => {
    if (!sessionStore.currentOrganisationId) {
      return false;
    }

    if (!props.editable) {
      return false;
    }

    return true;
  };

  const handleActivePatient = async (patientId: string) => {
    if (!shouldMakeApiCall()) {
      return;
    }

    if (patient.value && patient.value.id === patientId) {
      return;
    }
    if (props.item.patient && props.item.patient.id === patientId) {
      return;
    }

    try {
      const patientData = await patientService.fetchPatient(patientId);
      if (patientData) {
        patient.value = patientData;
        patientSearchText.value = formatPatientDisplay(patientData, false);
      } else {
        clearPatientData();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      logError(`Failed to fetch patient details: ${message}`);
      clearPatientData();
    }
  };

  watch(() => props.item.patientId, (newVal) => {
    editableFields.patientId = newVal || null;

    if (!newVal) {
      clearPatientData();
      return;
    }

    if (props.item.patient && props.item.patient.id === newVal) {
      const isPatientDeleted = props.item.patient.deleted ?? false;

      if (isPatientDeleted) {
        handleDeletedPatient(props.item.patient);
      } else {
        patient.value = props.item.patient;
        patientSearchText.value = formatPatientDisplay(props.item.patient, false);
      }
    } else {
      handleActivePatient(newVal);
    }
  }, { immediate: true });

  const handleUpdateItem = options?.useCartStore ? debounce(async (item: OrderItem) => {
    if (!webshopCartStore) {
      return;
    }

    let updatedItem = item;

    if (item.isStock === true) {
      const powerQuantities = parsePowerQuantities(item.powerQuantities);
      if (powerQuantities.length > 0) {
        const totalQuantity = calculateTotalQuantity(powerQuantities);
        updatedItem = structuredClone(item);
        updatedItem.quantity = totalQuantity;
      }
    }

    // Preserve productOptions.lensProperties from the original item
    // This ensures the MLS API order is maintained even after updates
    const originalItem = props.item;
    const hasOriginalLensProperties = originalItem.productOptions?.lensProperties;
    const missingUpdatedLensProperties = !updatedItem.productOptions?.lensProperties ||
      updatedItem.productOptions.lensProperties.length === 0;

    if (hasOriginalLensProperties && missingUpdatedLensProperties) {
      updatedItem = structuredClone(updatedItem);
      if (!updatedItem.productOptions) {
        updatedItem.productOptions = {};
      }
      updatedItem.productOptions.lensProperties = originalItem.productOptions.lensProperties;
    }

    const mergeAnalysis = analyzeMergeOperation(updatedItem, webshopCartStore.cartItems);
    try {
      if (mergeAnalysis.shouldMerge && mergeAnalysis.targetItem &&
          mergeAnalysis.mergedItem && mergeAnalysis.sourceItemId) {
        // Preserve lensProperties in merged item as well
        const missingMergedLensProperties = !mergeAnalysis.mergedItem.productOptions?.lensProperties ||
          mergeAnalysis.mergedItem.productOptions.lensProperties.length === 0;

        if (hasOriginalLensProperties && missingMergedLensProperties) {
          mergeAnalysis.mergedItem = structuredClone(mergeAnalysis.mergedItem);
          if (!mergeAnalysis.mergedItem.productOptions) {
            mergeAnalysis.mergedItem.productOptions = {};
          }
          mergeAnalysis.mergedItem.productOptions.lensProperties =
            originalItem.productOptions.lensProperties;
        }
        await webshopCartStore.updateItem(mergeAnalysis.targetItem.id, mergeAnalysis.mergedItem);
        await webshopCartStore.removeItem(mergeAnalysis.sourceItemId);
      } else {
        await webshopCartStore.updateItem(updatedItem.id, updatedItem);
      }
      showNotification(true);
    } catch (error) {
      showNotification(false);
    }
  }, 500) : (item: OrderItem) => {
    emit('update-item', item);
  };

  watch(() => editableFields.quantity, triggerUpdate);
  watch(() => editableFields.reference, triggerUpdate);
  watch(() => editableFields.packValue, triggerUpdate);
  watch(() => editableFields.patientId, triggerUpdate);

  return {
    isSelected,
    isUserEditingQuantity,
    isUserEditingReference,
    editableFields,
    isLoading,
    safeQuantity,
    displayOptions,
    packOptions,
    parsedPowerQuantities,
    validateReference,
    handleRemoveItem,
    handleUpdateItem,
    handleSelectionChange,
    triggerUpdate,
    handlePowerQuantitiesUpdate,
    getFallbackImage,
    patient,
    patientSearchText,
    patientSuggestions,
    isPatientSearchLoading,
    isPatientSetting,
    searchPatients,
    debouncedPatientSearch,
    handlePatientChange
  };
}
